import { posthog } from 'posthog-js';

import { getLandingParams } from './landing-params.js';

const POSTHOG_HOST = 'https://eu.i.posthog.com';

let inited = false;

/**
 * Initialise the posthog-js SDK. Idempotent — subsequent calls are no-ops
 * (the PostHog state machine guards against double-init too, but we belt-
 * and-suspenders here so any future call path stays safe). Absent `key` (no
 * `VITE_POSTHOG_KEY` in env) → no-op; the SDK is never loaded.
 *
 * Init runs only after the user grants `measurement` consent — see
 * `tag-system/design.md`'s PostHog state machine, which calls this on the
 * `idle → activeNoId` transition. There is no "load and stay opted out"
 * state, so the init-time `opt_out_capturing_by_default` flag is absent.
 *
 * `opt_out_persistence_by_default: true` is intentionally kept. It does NOT
 * change init-time posture (the SDK considers consent PENDING at init so
 * `isOptedOut` is false → persistence enabled → cookies + localStorage are
 * written normally). What it does do is gate the cleanup branch inside
 * `posthog.opt_out_capturing()`: when the user later revokes consent, the
 * SDK calls `_sync_opt_out_with_persistence` → `_is_persistence_disabled`
 * returns true (because `isOptedOut && opt_out_persistence_by_default`) →
 * `persistence.set_disabled(true)` → `persistence.remove()` → the
 * `ph_<token>_*` cookies/localStorage entries are wiped. Without this flag
 * the same revoke call leaves the cookies in place.
 *
 * See `posthog-integration/design.md` "Consent revoke and storage cleanup"
 * for the full chain.
 *
 * Right after init the landing URL's `utm_*` / `ref` parameters (snapshotted
 * at boot by `landing-params.ts`, or restored from the WorkOS `state` after
 * the sign-in round-trip) are registered as session super properties. This mirrors what the SDK does itself with campaign params it
 * finds in the URL at first capture — session-scoped, on every event — but
 * from the URL the visitor actually arrived on, which the router has long
 * since rewritten by the time a first-time visitor grants consent. Must stay
 * synchronous after `posthog.init`: the SDK captures its initial `$pageview`
 * on a `setTimeout`, and that pageview has to carry the properties.
 */
export function initPostHog(key: string | undefined): void {
  if (!key || inited) return;
  posthog.init(key, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    // Wipes ph_<token>_* persistence on later opt_out_capturing(). Does NOT
    // affect init-time posture (PENDING consent at init → persistence stays
    // enabled). See JSDoc above.
    opt_out_persistence_by_default: true,
    session_recording: {
      sampleRate: 1.0,
      maskAllInputs: true,
      maskTextSelector: '*',
      blockSelector: '.ph-no-capture',
    },
  });
  const landingParams = getLandingParams();
  if (Object.keys(landingParams).length > 0) posthog.register_for_session(landingParams);
  inited = true;
}

/**
 * Landing parameter → PostHog's own first-touch person property. Only the
 * campaign parameters the SDK itself derives `$initial_*` values for belong
 * here; caller-supplied set-once values take precedence over the SDK's
 * derivation (which reads the rewritten URL for a first-time visitor).
 * Anything else — `ref`, or a future custom parameter — is ours and lands
 * unprefixed as `initial_<param>`: the `$` prefix is reserved for PostHog's
 * properties.
 */
const POSTHOG_INITIAL_PERSON_PROPERTY: Readonly<Record<string, string>> = {
  utm_source: '$initial_utm_source',
  utm_medium: '$initial_utm_medium',
  utm_campaign: '$initial_utm_campaign',
  utm_term: '$initial_utm_term',
  utm_content: '$initial_utm_content',
};

/**
 * Bind subsequent events to a stable user id (the noma platform user_id).
 *
 * The landing attribution also lands on the person as set-once properties
 * (see `POSTHOG_INITIAL_PERSON_PROPERTY` for the naming). Set-once is
 * first-value-wins server-side, so an existing user's real first touch is
 * never overwritten.
 */
export function identifyPostHog(id: string): void {
  const landingParams = getLandingParams();
  const setOnce =
    Object.keys(landingParams).length > 0
      ? Object.fromEntries(
          Object.entries(landingParams).map(([key, value]) => [
            POSTHOG_INITIAL_PERSON_PROPERTY[key] ?? `initial_${key}`,
            value,
          ]),
        )
      : undefined;
  posthog.identify(id, undefined, setOnce);
}

/**
 * Set the SDK's built-in `$internal_or_test_user: true` person property when
 * the account's `users.is_internal_or_test` flag (served on `/me`) is set, so
 * the PostHog project's "filter out internal and test users" cohort excludes
 * the team from insights. The database owns the classification — see
 * docs/features/internal-test-user-flag/design.md. Must run after
 * `identifyPostHog` — the property has to land on the identified person, not
 * the anonymous pre-identify one.
 */
export function markInternalOrTestUserPostHog(isInternalOrTest: boolean): void {
  if (isInternalOrTest) posthog.setInternalOrTestUser();
}

/**
 * Discard the current `distinct_id` and persisted person properties. Called
 * on consent revoke + sign-out so the next session starts as a fresh
 * anonymous user.
 */
export function resetPostHog(): void {
  posthog.reset();
}

/** Turn capturing on (consent granted, re-grant from dormant). */
export function optInPostHog(): void {
  posthog.opt_in_capturing();
}

/** Turn capturing off (consent revoked). */
export function optOutPostHog(): void {
  posthog.opt_out_capturing();
}

/** Test-only: reset module state between tests. */
export function __resetInitedForTests(): void {
  inited = false;
}
